import React from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ISO6391 from "iso-639-1";
import { Upload, X } from "lucide-react";
import { Alert } from "react-bootstrap";

/**
 * Dynamic Agent Form Component
 * Renders form fields based on the variables object from agent data
 */
const DynamicAgentForm = ({
  variables,
  formData,
  setFormData,
  allLocations = [],
  imageUrl,
  imageError,
  isUploadingImage,
  fileInputRef,
  handleImageSelect,
  handleReplaceImage,
  removeImage,
}) => {
  // Language options
  const languageOptions = ISO6391.getAllNames().map(name => ({
    value: name,
    label: name,
  }));

  // Load location options for AsyncSelect
  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // Parse variables - can be comma-separated string, JSON string, or object
  const parseVariables = () => {
    if (!variables) return [];
    
    try {
      // If it's a string, check if it's comma-separated field names
      if (typeof variables === 'string') {
        // Check if it's a comma-separated list (e.g., "location, words_char_lenght")
        if (variables.includes(',') && !variables.trim().startsWith('[') && !variables.trim().startsWith('{')) {
          const fieldNames = variables.split(',').map(name => name.trim()).filter(name => name);
          // Convert to field objects
          return fieldNames.map(fieldName => {
            // Determine field type based on field name
            let fieldType = 'text';
            let fieldLabel = fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            // Auto-detect field types based on common patterns
            if (fieldName.toLowerCase().includes('location')) {
              fieldType = 'location';
            } else if (fieldName.toLowerCase().includes('language') || fieldName.toLowerCase().includes('lang')) {
              fieldType = 'language';
            } else if (fieldName.toLowerCase().includes('image') || fieldName.toLowerCase().includes('file') || fieldName.toLowerCase().includes('photo')) {
              fieldType = 'image';
            } else if ( fieldName.toLowerCase().includes('count') || fieldName.toLowerCase().includes('number') || fieldName.toLowerCase().includes('words') || fieldName.toLowerCase().includes('char')) {
              fieldType = 'number';
            } else if (fieldName.toLowerCase().includes('description') || fieldName.toLowerCase().includes('content') || fieldName.toLowerCase().includes('prompt') || fieldName.toLowerCase().includes('message') || fieldName.toLowerCase().includes('text')) {
              fieldType = 'textarea';
            } else if (fieldName.toLowerCase().includes('select') || fieldName.toLowerCase().includes('choice') || fieldName.toLowerCase().includes('option')) {
              fieldType = 'select';
            }
            
            return {
              name: fieldName,
              type: fieldType,
              label: fieldLabel,
              required: false, // Default to optional unless specified
            };
          });
        }
        
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(variables);
          return Array.isArray(parsed) ? parsed : (parsed.fields || [parsed]);
        } catch (e) {
          // If not JSON, treat as single field name
          return [{
            name: variables.trim(),
            type: 'text',
            label: variables.trim().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            required: false,
          }];
        }
      }
      
      // If it's already an object
      if (typeof variables === 'object') {
        return Array.isArray(variables) ? variables : (variables.fields || [variables]);
      }
      
      return [];
    } catch (e) {
      console.error('Error parsing variables:', e);
      return [];
    }
  };

  const fields = parseVariables();

  // Render field based on type
  const renderField = (field) => {
    const fieldName = field.name || field.id || field.key;
    const fieldLabel = field.label || field.title || fieldName;
    const fieldType = field.type || 'text';
    const isRequired = field.required === true; // Only required if explicitly set to true
    const placeholder = field.placeholder || field.description || `Enter ${fieldLabel.toLowerCase()}...`;
    const fieldValue = formData[fieldName] || '';

    switch (fieldType.toLowerCase()) {
      case 'text':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
              required={isRequired}
            />
          </Form.Group>
        );

      case 'textarea':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={field.rows || 3}
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
              required={isRequired}
            />
          </Form.Group>
        );

      case 'select':
        const options = field.options || [];
        const selectOptions = Array.isArray(options) 
          ? options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt)
          : [];
        
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Select
              options={selectOptions}
              value={fieldValue}
              onChange={(selected) => setFormData({ ...formData, [fieldName]: selected })}
              isClearable={!isRequired}
              isSearchable
              placeholder={placeholder}
            />
          </Form.Group>
        );

      case 'language':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
              {isRequired && <span className="text-muted"> (Required)</span>}
            </Form.Label>
            <Select
              options={languageOptions}
              value={fieldValue}
              onChange={(selected) => setFormData({ ...formData, [fieldName]: selected })}
              isClearable={!isRequired}
              isSearchable
              placeholder="Type to search language..."
            />
          </Form.Group>
        );

      case 'location':
      case 'location_focus':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadLocationOptions}
              value={fieldValue}
              onChange={(selected) => setFormData({ ...formData, [fieldName]: selected })}
              isClearable={!isRequired}
              isSearchable
              placeholder="Type to search location..."
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Type to search..."
                  : "No locations found for that query"
              }
              loadingMessage={() => "Searching locations..."}
            />
          </Form.Group>
        );

      case 'number':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              type="number"
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
              required={isRequired}
              min={field.min || 0}
              max={field.max}
            />
          </Form.Group>
        );

      case 'image':
      case 'file':
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {!isRequired && <span className="text-muted"> (Optional)</span>}
            </Form.Label>
            <div className="image-upload-container">
              {!imageUrl ? (
                <div 
                  className="image-upload-area"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                  <Upload size={24} className="upload-icon" />
                  <p className="upload-text">Click to upload image</p>
                  <small className="text-muted">
                    Supported: JPEG, PNG (Max 10MB)
                  </small>
                </div>
              ) : (
                <div className="image-preview-container">
                  <img 
                    src={imageUrl} 
                    alt="Content preview" 
                    className="image-preview"
                  />
                  <div className="image-actions">
                    <button
                      type="button"
                      className="replace-image-btn"
                      onClick={handleReplaceImage}
                      title="Replace image"
                    >
                      <Upload size={14} />
                    </button>
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={removeImage}
                      title="Remove image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
            {imageError && (
              <Alert variant="danger" className="mt-2 py-2">
                {imageError}
              </Alert>
            )}
            {isUploadingImage && (
              <div className="text-center mt-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
                <span className="ms-2">Uploading image...</span>
              </div>
            )}
          </Form.Group>
        );

      default:
        // Default to text input
        return (
          <Form.Group key={fieldName} className="mb-3">
            <Form.Label>
              {fieldLabel}
              {isRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={placeholder}
              value={fieldValue}
              onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
              required={isRequired}
            />
          </Form.Group>
        );
    }
  };

  return (
    <div className="mb-4">
      <h6 className="section-title mb-3">Provide Context</h6>
      
      {/* Static Title and Prompt fields - always shown for all agents and required */}
      <Form.Group className="mb-3">
        <Form.Label>
          Title
          <span className="text-danger"> *</span>
        </Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter a title for your content..."
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          Prompt
          <span className="text-danger"> *</span>
        </Form.Label>
        <Form.Control
          type="textarea"
          as="textarea"
          rows={6}
          placeholder="Enter your prompt or question here..."
          value={formData.prompt || ''}
          onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
          required
        />
      </Form.Group>

      {/* Dynamic fields from variables - only show if there are fields */}
      {fields && fields.length > 0 && fields.map((field) => renderField(field))}
    </div>
  );
};

export default DynamicAgentForm;

